import { type ReactNode, useMemo, useState } from 'react';
import { useResponsive } from './useResponsive';
import { useContextMenu, type RowMenuItem } from '../components/RowContextMenu';

/**
 * Column definition for ResponsiveTable.
 *
 * On desktop, each column renders as a table column.
 * On mobile, columns are grouped by their `mobile` role:
 *   - 'title'     → primary text at top-left of card (bold)
 *   - 'accessory' → top-right of card (status badges, scores, etc.)
 *   - 'meta'      → bottom meta line (compact, flex-wrap) — default
 *   - 'hide'      → desktop only
 */
export interface ResponsiveColumn<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  /** Override render specifically for the mobile card view (e.g., to add a prefix like "Due ..."). */
  mobileRender?: (row: T) => ReactNode;
  mobile?: 'title' | 'accessory' | 'meta' | 'hide';
  /**
   * Optional className applied to the <td> on desktop.
   *
   * Beside it, every cell and header already carries `dt-key-<key>` — so a table
   * can size or align a column BY NAME. Positional `nth-child` rules were the
   * alternative and they are silently wrong the moment a caller hides a column:
   * the plan table's "widen the passes" rule spent its width on Board/Grade on
   * every surface that omitted two columns before it.
   *
   * `dt-key-`, not `dt-col-`: the latter is where hand-written per-column classes
   * already live (`dt-col-date`, `dt-col-subject` in the admin console's
   * Operations tables) and their CSS is global, so generating into that namespace
   * would have silently applied one table's column widths to every other table
   * with a column of the same name.
   */
  tdClassName?: string;
  /** Make this column's desktop header clickable to sort. Requires `sortValue`. */
  sortable?: boolean;
  /** Value used for sorting this column. Numbers sort numerically (timestamps,
   *  scores); strings sort with localeCompare. Empty string / null / NaN sort
   *  last regardless of direction. */
  sortValue?: (row: T) => string | number | null | undefined;
}

export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

export interface ResponsiveTableProps<T> {
  rows: T[];
  columns: ResponsiveColumn<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** When provided, the row with matching key is rendered with `.selected-row` class. */
  selectedRowKey?: string;
  /** Extra class per row, applied to the desktop `<tr>` and the mobile card
   *  alike (e.g. an accent on rows that need attention). */
  rowClassName?: (row: T) => string;
  /** Gates `onRowClick` per row. Without it every row is clickable whenever
   *  `onRowClick` is set — including rows the handler ignores, which still get
   *  the `clickable-row` affordance and read as interactive when they aren't. */
  rowClickable?: (row: T) => boolean;
  /** `data-testid` applied to each row (same id on all rows, as is idiomatic for list selectors). */
  rowTestId?: string;
  /** Extra class for the root element (table on desktop, div on mobile). */
  className?: string;
  /** Initial sort applied on mount (column must be `sortable` with a `sortValue`). */
  initialSort?: SortState;
  /** Applied when the sorted column compares equal — e.g. keep an alphabetical
   *  name order among students who all scored the same. Direction-independent:
   *  the tie-break is not reversed when sorting descending. */
  sortTieBreak?: (a: T, b: T) => number;
  /**
   * How to present the rows on a wide screen.
   *   'table' (default) — columns, with sortable headers
   *   'grid'            — the CARD view at every width, flowing into as many
   *                       columns as fit
   *
   * The cards are the same ones mobile already shows, driven by the same `mobile`
   * role on each column ('title' / 'accessory' / 'meta' / 'hide'), so a caller
   * switches view without redefining anything. Note a grid has no headers, so
   * columns lose their click-to-sort affordance there; `initialSort` still applies.
   */
  view?: 'table' | 'grid';
  /**
   * Right-click menu for a row — the actions that do not earn a column.
   *
   * The same contract `DataTable` uses, so a table moved between the two keeps
   * its menu: return the items for this row, or an empty list for a row that
   * has none. Rendering and dismissal belong to the table, not the caller.
   */
  rowMenu?: (row: T) => RowMenuItem[];
}

/** Empty (unsortable) cell values always sort to the bottom. */
function sortCompare(a: string | number | null | undefined, b: string | number | null | undefined): number {
  const aEmpty = a === '' || a == null || (typeof a === 'number' && Number.isNaN(a));
  const bEmpty = b === '' || b == null || (typeof b === 'number' && Number.isNaN(b));
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

export function ResponsiveTable<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  selectedRowKey,
  rowClassName,
  rowClickable,
  rowTestId,
  className,
  initialSort,
  sortTieBreak,
  view = 'table',
  rowMenu,
}: ResponsiveTableProps<T>) {
  const { isMobile } = useResponsive();
  const { openMenu, menuNode } = useContextMenu();

  /** No handler at all when the row offers nothing, so the browser's own menu
   *  still works where ours would be empty. */
  const openRowMenu = (row: T) => {
    if (!rowMenu) return undefined;
    return (e: React.MouseEvent) => {
      const items = rowMenu(row);
      if (items.length) openMenu(e, items);
    };
  };
  const [sort, setSort] = useState<SortState | null>(initialSort ?? null);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find(c => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const dir = sort.dir === 'desc' ? -1 : 1;
    return [...rows].sort((ra, rb) => {
      const base = sortCompare(col.sortValue!(ra), col.sortValue!(rb));
      return base === 0 ? (sortTieBreak?.(ra, rb) ?? 0) : base * dir;
    });
  }, [rows, columns, sort, sortTieBreak]);

  // Cycle a sortable header: asc → desc → none (back to natural order).
  const toggleSort = (key: string) => {
    setSort(prev => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  // Mobile has always used cards; `view='grid'` asks for them on desktop too.
  if (isMobile || view === 'grid') {
    return (
      <MobileCardList
        rows={sortedRows}
        columns={columns}
        rowKey={rowKey}
        onRowClick={onRowClick}
        selectedRowKey={selectedRowKey}
        rowClassName={rowClassName}
        rowClickable={rowClickable}
        rowTestId={rowTestId}
        className={`${view === 'grid' ? 'card-grid' : ''} ${className ?? ''}`.trim() || undefined}
        rowMenu={rowMenu}
      />
    );
  }

  return (
    <>
    {/* The same scroller `DataTable` wraps its table in, and for the same
        reason: a table that still cannot fit after wrapping must pan inside its
        own box. Without it the table either clips — columns silently gone — or
        drags the whole content pane sideways, since `.sidebar-layout-content`
        computes `overflow-x: auto`. A no-op at any width where the table fits. */}
    <div className="dt-scroll">
    <table className={`dt ${className ?? ''}`.trim()}>
      <thead>
        <tr>
          {columns.map(col => {
            if (!col.sortable || !col.sortValue) {
              return <th key={col.key} className={`dt-key-${col.key}`}>{col.header}</th>;
            }
            const active = sort?.key === col.key;
            const indicator = active ? (sort!.dir === 'asc' ? '▲' : '▼') : '↕';
            return (
              <th
                key={col.key}
                className={`dt-key-${col.key} dt-sortable${active ? ' dt-sorted' : ''}`}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => toggleSort(col.key)}
                aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                {col.header}
                <span className="dt-sort-ind" style={{ marginLeft: 4, opacity: active ? 0.9 : 0.4 }}>{indicator}</span>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map(row => {
          const key = rowKey(row);
          const isSelected = selectedRowKey === key;
          const clickable = !!onRowClick && (rowClickable?.(row) ?? true);
          const classes = [
            clickable ? 'clickable-row' : null,
            isSelected ? 'selected-row' : null,
            rowClassName?.(row) || null,
          ].filter(Boolean).join(' ');
          return (
            <tr
              key={key}
              className={classes || undefined}
              data-testid={rowTestId}
              onClick={clickable ? () => onRowClick!(row) : undefined}
              onContextMenu={openRowMenu(row)}
            >
              {columns.map(col => {
                const content = col.render(row);
                return (
                  <td
                    key={col.key}
                    className={`dt-key-${col.key}${col.tdClassName ? ` ${col.tdClassName}` : ''}`}
                    // Columns that truncate (e.g. .td-title) would otherwise hide
                    // the rest of the value with no way to read it. Only plain
                    // text can be a tooltip — rendered nodes are skipped rather
                    // than stringified into "[object Object]".
                    title={typeof content === 'string' || typeof content === 'number'
                      ? String(content)
                      : undefined}
                  >
                    {content}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
    {menuNode}
    </>
  );
}

function MobileCardList<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  selectedRowKey,
  rowClassName,
  rowClickable,
  rowTestId,
  className,
  rowMenu,
}: ResponsiveTableProps<T>) {
  const { openMenu, menuNode } = useContextMenu();
  const titleCol = columns.find(c => c.mobile === 'title');
  const accessoryCols = columns.filter(c => c.mobile === 'accessory');
  const metaCols = columns.filter(c => c.mobile === undefined || c.mobile === 'meta');

  const renderMobile = (col: ResponsiveColumn<T>, row: T) =>
    col.mobileRender ? col.mobileRender(row) : col.render(row);

  return (
    <div className={`mobile-card-list ${className ?? ''}`.trim()}>
      {rows.map(row => {
        const clickable = !!onRowClick && (rowClickable?.(row) ?? true);
        const key = rowKey(row);
        const isSelected = selectedRowKey === key;
        const commonProps = {
          className: ['mobile-card', isSelected ? 'selected-row' : '', rowClassName?.(row) || '']
            .filter(Boolean).join(' '),
          'data-testid': rowTestId,
          onClick: clickable ? () => onRowClick(row) : undefined,
          // Cards get the same menu the rows do — this branch also serves
          // `view='grid'` on a desktop, where a right-click is expected.
          onContextMenu: rowMenu
            ? (e: React.MouseEvent) => {
              const items = rowMenu(row);
              if (items.length) openMenu(e, items);
            }
            : undefined,
        } as const;

        // The mobile card title truncates too; same rule as the table cells.
        const titleContent = titleCol ? renderMobile(titleCol, row) : null;
        const mobileTitleText = typeof titleContent === 'string' || typeof titleContent === 'number'
          ? String(titleContent)
          : undefined;

        const content = (
          <>
            <div className="mobile-card-top">
              {titleCol && (
                <span className="mobile-card-title" title={mobileTitleText}>{titleContent}</span>
              )}
              {accessoryCols.map(col => (
                <span key={col.key} className="mobile-card-accessory">
                  {renderMobile(col, row)}
                </span>
              ))}
            </div>
            {metaCols.length > 0 && (
              <div className="mobile-card-meta">
                {metaCols.map(col => {
                  const value = renderMobile(col, row);
                  if (value === null || value === undefined || value === '' || value === '-') return null;
                  return <span key={col.key}>{value}</span>;
                })}
              </div>
            )}
          </>
        );

        // Clickable rows use div[role=button], not <button>: a cell may render
        // its own <button> (e.g. a row action), and a button cannot nest a
        // button (invalid HTML + hydration warning). Keyboard handling keeps it
        // operable; .mobile-card[role=button]:focus-visible gives it a focus ring.
        return clickable ? (
          <div
            key={key}
            role="button"
            tabIndex={0}
            {...commonProps}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick!(row); }
            }}
          >{content}</div>
        ) : (
          <div key={key} {...commonProps}>{content}</div>
        );
      })}
      {menuNode}
    </div>
  );
}
